import http from "./http-common";

/**
 * VOC — Voice of Customer
 *
 * ฝั่งผู้ใช้เรียก getSetting / submit เท่านั้น (ไม่ต้อง login)
 * ที่เหลือเป็นของหน้าแอดมิน ต้องแนบ token
 */

/** ตัวเลือกที่ต้องตรงกับ enum ใน backend เป๊ะ */
export const CATEGORIES = [
  { value: "compliment", label_th: "ชมเชย", label_en: "Compliment" },
  { value: "inquiry", label_th: "ติดต่อสอบถาม", label_en: "Inquiry" },
  { value: "suggestion", label_th: "เสนอแนะ", label_en: "Suggestion" },
  { value: "complaint", label_th: "ร้องเรียน", label_en: "Complaint" },
];

export const CONTACT_STATUSES = [
  { value: "student", label_th: "นักศึกษา", label_en: "Student" },
  { value: "staff", label_th: "บุคลากร", label_en: "Staff" },
  { value: "alumni", label_th: "นักศึกษาเก่า", label_en: "Alumni" },
  { value: "general", label_th: "บุคคลทั่วไป", label_en: "General Public" },
];

export const STATUSES = [
  { value: "new", label_th: "เรื่องใหม่", label_en: "New" },
  { value: "in_progress", label_th: "กำลังดำเนินการ", label_en: "In Progress" },
  { value: "closed", label_th: "ปิดเรื่อง", label_en: "Closed" },
];

/**
 * ตัวเลือกที่ไม่ผูกกับข้อมูลจริงของ มจธ. จึงกำหนดไว้ตรงนี้ได้เลย
 *
 * ค่าที่บันทึกลงฐานข้อมูลเป็นภาษาไทยเสมอ ส่วน label_en ใช้แค่ตอนแสดงผลหน้า /en
 * ทำแบบนี้เพื่อให้ข้อมูลในหน้าแอดมิน (ซึ่งเป็นภาษาไทย) เป็นชุดเดียวกันทั้งหมด
 * ไม่ปนกันว่าคนกรอกจากหน้าไทยหรือหน้าอังกฤษ
 */
export const EDUCATION_LEVELS = [
  { value: "ปริญญาตรี", label_en: "Bachelor's Degree" },
  { value: "ปริญญาโท", label_en: "Master's Degree" },
  { value: "ปริญญาเอก", label_en: "Doctoral Degree" },
];

export const YEARS_OF_STUDY = [
  { value: "ชั้นปีที่ 1", label_en: "Year 1" },
  { value: "ชั้นปีที่ 2", label_en: "Year 2" },
  { value: "ชั้นปีที่ 3", label_en: "Year 3" },
  { value: "ชั้นปีที่ 4", label_en: "Year 4" },
  { value: "สูงกว่าชั้นปีที่ 4", label_en: "Above Year 4" },
];

export const STAFF_TYPES = [
  { value: "สายวิชาการ", label_en: "Academic staff" },
  { value: "สายสนับสนุน", label_en: "Support staff" },
  { value: "ลูกจ้าง", label_en: "Employee" },
  { value: "อื่นๆ", label_en: "Other" },
];

/** เปลี่ยนรายการข้างบนให้เป็น { value, label } ตามภาษาที่กำลังดูอยู่ */
export function localizeOptions(list, lang) {
  return list.map((o) => ({
    value: o.value,
    label: lang === "th" ? o.value : o.label_en || o.value,
  }));
}

/**
 * รายชื่อคณะ/หน่วยงานมาจาก VOC Setting เพราะเป็นข้อมูลจริงของมหาวิทยาลัย
 * ที่ไม่ควรฝังไว้ในโค้ด — กรอกบรรทัดละ 1 ตัวเลือกในหน้าจัดการ
 * ถ้ายังไม่ได้กรอก ช่องนั้นจะกลายเป็นช่องพิมพ์เองแทนดรอปดาวน์
 */
export function parseOptions(text) {
  if (!text) return [];
  return String(text)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * จับคู่รายการภาษาไทยกับภาษาอังกฤษตามลำดับบรรทัด
 *
 * บรรทัดที่ 1 ของช่องอังกฤษคือคำแปลของบรรทัดที่ 1 ของช่องไทย บรรทัดไหน
 * ไม่ได้แปลไว้ (หรือไม่ได้กรอกช่องอังกฤษเลย) จะโชว์ภาษาไทยของบรรทัดนั้นแทน
 * ค่าที่ส่งเข้าระบบยังเป็นภาษาไทยเสมอ
 */
export function parseOptionPairs(thText, enText, lang) {
  const th = parseOptions(thText);
  const en = parseOptions(enText);

  return th.map((value, i) => ({
    value,
    label: lang === "th" ? value : en[i] || value,
  }));
}

/**
 * หยิบข้อความจาก VOC Setting ตามภาษาที่กำลังดูอยู่
 *
 * ช่องภาษาอังกฤษเว้นว่างได้ ถ้าเว้นไว้จะใช้ภาษาไทยแทน เพื่อไม่ให้หน้า /en
 * กลายเป็นหน้าว่างเปล่าเวลายังแปลไม่เสร็จ
 */
export function pickLang(setting, key, lang) {
  if (!setting) return "";

  if (lang !== "th") {
    const en = setting[`${key}_EN`];
    if (en && String(en).trim()) return en;
  }

  return setting[key] || "";
}

export const MAX_ATTACHMENTS = 3;
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg"];

const labelOf = (list, value, lang) => {
  const found = list.find((i) => i.value === value);
  if (!found) return value || "";
  return lang === "th" ? found.label_th : found.label_en;
};

export const categoryLabel = (v, lang) => labelOf(CATEGORIES, v, lang);
export const contactStatusLabel = (v, lang) => labelOf(CONTACT_STATUSES, v, lang);
export const statusLabel = (v, lang) => labelOf(STATUSES, v, lang);

// eslint-disable-next-line
export default new (class VocService {
  /** ข้อความบนหน้า VOC ที่แก้ได้จาก Strapi */
  async getSetting() {
    return await http
      .get("/voc-setting?pLevel")
      .then((res) => res.data?.data || null)
      .catch((error) => {
        console.log(error);
        return null;
      });
  }

  /**
   * ส่งเรื่องเข้าระบบ
   *
   * ส่งเป็น multipart เสมอ — ไฟล์ไปพร้อมข้อมูลในคำขอเดียว
   * backend จะอัปโหลดให้เอง ไม่ต้องเปิด /api/upload สาธารณะ
   *
   * คืน { ok, caseNumber } หรือ { ok: false, errors, message }
   */
  async submit(payload, files = []) {
    const form = new FormData();
    form.append("data", JSON.stringify(payload));
    files.forEach((file) => form.append("files", file));

    try {
      const res = await http.post("/voc-feedbacks/submit", form);
      return { ok: true, caseNumber: res.data?.data?.caseNumber || "" };
    } catch (error) {
      const body = error?.response?.data?.error;
      return {
        ok: false,
        errors: body?.details?.errors || {},
        message: body?.message || "ส่งข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      };
    }
  }
})();
